// Normalizes a ui-1 or ui-2 observation into one view model the components draw.
// ui-2 fields are used as given. For ui-1 packages, a small set of clearly marked
// heuristics infers seats, owners and zone kinds from IDs and labels; nothing here
// names a specific game.

import type { Board, CatalogEntry, Description, Observation, Scalar, UiCounter, UiItem, UiObject, UiZone, ZoneKind } from '../protocol/ui';
import type { PublicProfile, PublicSeatProfile } from '../protocol/wire';

export type Placement = 'board' | 'public' | 'self' | 'opponent';

export interface DetailRow { key: string; value: string }

export interface ObjectView {
  id: string;
  label: string;
  title: string;
  subtitle: string | null;
  kind: string;
  zoneId: string;
  owner: string | null;
  faceDown: boolean;
  count: number | null;
  catalog: CatalogEntry | null;
  catalogKey: string | null;
  /** Live object fields as sent (ui-2), used by presentation bindings. */
  fields: Record<string, Scalar>;
  stack: string | null;
  details: DetailRow[];
  tags: string[];
  at: string | null;
  ref: string | null;
  points: string | null;
  costs: { key: string; value: number }[];
}

export interface ZoneView {
  id: string;
  label: string;
  kind: ZoneKind;
  owner: string | null;
  visibility: 'public' | 'owner' | 'hidden';
  count: number | null;
  boardId: string | null;
  placement: Placement;
  objects: ObjectView[];
}

export interface SeatView { id: string; label: string; active: boolean; status: string | null; isSelf: boolean; profile?: PublicProfile }
export interface CounterView { id: string; label: string; value: Scalar; owner: string | null; key: string | null; max: number | null }

export interface TableModel {
  /** Public identity only; absent or malformed identity disables restoration across choices. */
  readonly matchId?: string | null;
  title: string;
  status: string;
  actor: string | null;
  /** Explicit pending owners for independent packages; absent metadata falls back to the cursor. */
  decisionOwners?: string[];
  auxiliaryActions?: string[];
  finished: boolean;
  selfSeat: string | null;
  seats: SeatView[];
  zones: ZoneView[];
  objects: Map<string, ObjectView>;
  counters: CounterView[];
  boards: Board[];
  resources: { key: string; label: string }[];
  /** Describe glossary (static game help), when the package supplies one. */
  glossary?: { term: string; text: string }[];
  result: Observation['ui']['result'] | null;
  interfaceVersion: 'ui-1' | 'ui-2';
}

const ZONE_KINDS: ZoneKind[] = ['hand', 'row', 'grid', 'stack', 'area', 'track', 'board', 'supply', 'discard'];

export function buildTable(observation: Observation, description: Description | null, selfSeat: string | null, context?: { matchId?: string | null; profiles?: PublicSeatProfile[] }): TableModel {
  const ui = observation.ui;
  const isUi2 = Array.isArray(ui.objects) && Array.isArray(ui.seats);
  const self = selfSeat && selfSeat !== 'spectator' ? selfSeat : null;
  const seatIds = isUi2 ? ui.seats!.map(s => s.id) : inferSeats(observation);
  const seatLabel = (id: string) => description?.seats.find(s => s.id === id)?.label ?? ui.seats?.find(s => s.id === id)?.label ?? humanize(id);
  const seats: SeatView[] = isUi2
    ? ui.seats!.map(s => ({ id: s.id, label: s.label, active: s.active, status: s.status ?? null, isSelf: s.id === self }))
    : seatIds.map(id => ({ id, label: seatLabel(id), active: observation.actor === id, status: null, isSelf: id === self }));
  for (const seat of seats) { const profile = context?.profiles?.find(p => p.seat === seat.id)?.profile; if (profile) seat.profile = profile; }

  const catalog = new Map((description?.catalog ?? []).map(c => [c.key, c]));
  const resources = description?.resources ?? [];
  const objects = new Map<string, ObjectView>();
  const zones: ZoneView[] = ui.zones.map(zone => {
    const owner = zone.owner ?? (isUi2 ? null : inferOwner(zone.id, zone.label, seatIds, self));
    const kind = zone.kind ?? inferZoneKind(zone, owner);
    const visibility = zone.visibility ?? 'public';
    const view: ZoneView = {
      id: zone.id, label: owner && !isUi2 ? stripSeat(zone.label, owner) : zone.label, kind, owner, visibility,
      count: zone.count ?? null, boardId: zone.board ?? null,
      placement: kind === 'board' && zone.board && description?.boards.some(b => b.id === zone.board) ? 'board' : owner === null ? 'public' : owner === self ? 'self' : 'opponent',
      objects: [],
    };
    return view;
  });
  const zoneById = new Map(zones.map(z => [z.id, z]));

  if (isUi2) {
    for (const o of ui.objects!) {
      const view = objectFromUi2(o, catalog, resources);
      objects.set(view.id, view);
    }
    for (const z of ui.zones) {
      const zv = zoneById.get(z.id)!;
      zv.objects = z.items.map(i => objects.get(i.id)).filter((o): o is ObjectView => !!o);
    }
    for (const o of objects.values()) {
      const zv = zoneById.get(o.zoneId);
      if (zv && zv.kind === 'board' && !zv.objects.includes(o)) zv.objects.push(o);
    }
  } else {
    for (const z of ui.zones) {
      const zv = zoneById.get(z.id)!;
      zv.objects = z.items.map(item => {
        const view = objectFromUi1(item, zv);
        if (!objects.has(view.id)) objects.set(view.id, view);
        return view;
      });
    }
  }

  const counters = ui.counters.map((c, i) => counterView(c, i, isUi2, seatIds));
  // Inside a player's own area the seat name in a counter label is redundant.
  for (const c of counters) {
    const seat = c.owner ? seats.find(s => s.id === c.owner) : undefined;
    if (seat && c.label.toLowerCase().startsWith(seat.label.toLowerCase() + ' ')) c.label = capitalize(c.label.slice(seat.label.length + 1));
  }
  return {
    matchId: typeof context?.matchId === 'string' && /^[0-9a-f]{32}$/i.test(context.matchId) ? context.matchId.toLowerCase() : null,
    title: ui.title, status: ui.status, actor: observation.actor,
    decisionOwners: Array.isArray(observation.decisionOwners) ? observation.decisionOwners.filter((id): id is string => typeof id === 'string' && seatIds.includes(id)) : observation.actor ? [observation.actor] : [],
    auxiliaryActions: Array.isArray(observation.auxiliaryActions) ? observation.auxiliaryActions.filter((kind): kind is string => typeof kind === 'string') : [],
    finished: observation.finished, selfSeat: self,
    seats, zones, objects, counters, boards: description?.boards ?? [], resources,
    ...(description?.glossary?.length ? { glossary: description.glossary } : {}),
    result: ui.result ?? null, interfaceVersion: isUi2 ? 'ui-2' : 'ui-1',
  };
}

/** The filtered, server-validated ownership boundary, independent of the display cursor. */
export function ownsDecision(table: Pick<TableModel, 'selfSeat' | 'decisionOwners' | 'actor'>): boolean {
  return !!table.selfSeat && (table.decisionOwners ?? (table.actor ? [table.actor] : [])).includes(table.selfSeat);
}

function objectFromUi2(o: UiObject, catalog: Map<string, CatalogEntry>, resources: { key: string }[]): ObjectView {
  const entry = o.catalog ? catalog.get(o.catalog) ?? null : null;
  const fields = { ...(entry?.fields ?? {}), ...(o.fields ?? {}) };
  const costs = resources
    .map(r => ({ key: r.key, value: Number(fields[`cost.${r.key}`] ?? 0) }))
    .filter(c => Number.isFinite(c.value) && c.value > 0);
  const details = Object.entries(o.fields ?? {})
    .filter(([k]) => !k.startsWith('cost.') && k !== 'points')
    .map(([key, value]) => ({ key: humanize(key), value: formatScalar(value) }));
  const points = fields.points !== undefined ? formatScalar(fields.points) : null;
  return {
    id: o.id, label: o.label, title: entry?.name ?? shortTitle(o.label), subtitle: entry?.text ?? null,
    kind: o.kind ?? 'card', zoneId: o.zone ?? '', owner: o.owner ?? null, faceDown: o.face === 'down', count: o.count ?? null,
    catalog: entry, catalogKey: o.catalog ?? null, fields: o.fields ?? {}, stack: o.stack ?? null, details, tags: [...(entry?.tags ?? []), ...(o.tags ?? [])], at: o.at ?? null, ref: o.ref ?? null, points, costs,
  };
}

const UI1_SKIPPED_FIELDS = new Set(['id', 'label']);

function objectFromUi1(item: UiItem, zone: ZoneView): ObjectView {
  const details: DetailRow[] = [];
  let points: string | null = null;
  const costs: { key: string; value: number }[] = [];
  for (const [key, value] of Object.entries(item)) {
    if (UI1_SKIPPED_FIELDS.has(key) || value === null || value === undefined) continue;
    if (key === 'points' && (typeof value === 'number' || typeof value === 'string')) { points = String(value); continue; }
    if (key === 'cost' && value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) if (typeof v === 'number' && v > 0) costs.push({ key: k, value: v });
      continue;
    }
    const text = formatValue(value);
    if (text !== '') details.push({ key: humanize(key), value: text });
  }
  return {
    id: item.id, label: item.label, title: shortTitle(item.label), subtitle: null, kind: 'card', zoneId: zone.id,
    owner: zone.owner, faceDown: false, count: null, catalog: null, catalogKey: null, fields: {}, stack: null, details, tags: [], at: null, ref: null, points, costs,
  };
}

function counterView(c: UiCounter, index: number, isUi2: boolean, seats: string[]): CounterView {
  let owner = c.owner ?? null;
  let label = c.label;
  if (!isUi2 && !owner) {
    const seat = seats.find(s => label.startsWith(s + ' '));
    if (seat) { owner = seat; label = label.slice(seat.length + 1); }
  }
  return { id: c.id ?? `counter-${index}`, label: capitalize(label), value: c.value, owner, key: c.key ?? null, max: c.max ?? null };
}

// ---- ui-1 heuristics -------------------------------------------------------------

function inferSeats(observation: Observation): string[] {
  const top = observation['seats'];
  if (Array.isArray(top) && top.every(s => typeof s === 'string')) return top as string[];
  const found = new Set<string>();
  for (const zone of observation.ui.zones) {
    const m = /:([A-Za-z][A-Za-z0-9_-]*)$/.exec(zone.id);
    if (m?.[1]) found.add(m[1]);
  }
  if (observation.actor) found.add(observation.actor);
  return [...found];
}

function inferOwner(id: string, label: string, seats: string[], self: string | null): string | null {
  for (const seat of seats) if (id.endsWith(':' + seat) || id.startsWith(seat + ':')) return seat;
  if (self && (id === 'hand' || /^your\b/i.test(label))) return self;
  return null;
}

function inferZoneKind(zone: UiZone, owner: string | null): ZoneKind {
  const text = `${zone.id} ${zone.label}`.toLowerCase();
  if (/\bhand\b/.test(text)) return 'hand';
  if (/\bdeck\b|\bdraw pile\b/.test(text)) return 'stack';
  if (/\bdiscard\b/.test(text)) return 'discard';
  if (owner) return 'area';
  // A ui-1 package has no board geometry, so 'board' is never inferred.
  return ZONE_KINDS.includes(zone.id as ZoneKind) && zone.id !== 'board' ? (zone.id as ZoneKind) : 'row';
}

// ---- text helpers ----------------------------------------------------------------

const humanized = new Map<string, string>();
export function humanize(id: string): string {
  const hit = humanized.get(id); if (hit !== undefined) return hit;
  if (humanized.size > 2000) humanized.clear();
  const text = humanizeText(id); humanized.set(id, text); return text;
}
function humanizeText(id: string): string {
  const spaced = id.replace(/[_:.-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Za-z])(\d)/g, '$1 $2').trim();
  return capitalize(spaced.toLowerCase());
}

export function capitalize(text: string): string {
  return text.length ? text[0]!.toUpperCase() + text.slice(1) : text;
}

function stripSeat(label: string, seat: string): string {
  return label.startsWith(seat + ' ') ? capitalize(label.slice(seat.length + 1)) : label;
}

/** First clause of a long accessible label, used as a card title. */
export function shortTitle(label: string): string {
  const cut = label.split(/ \(| — | - |; /)[0] ?? label;
  return cut.length > 48 ? cut.slice(0, 47) + '…' : cut;
}

function formatScalar(value: Scalar): string {
  return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
}

function formatValue(value: unknown, depth = 0): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return formatScalar(value);
  if (depth > 3) return '…';
  if (Array.isArray(value)) return value.map(v => formatValue(v, depth + 1)).filter(Boolean).join(', ');
  return Object.entries(value as Record<string, unknown>)
    .map(([k, v]) => { const t = formatValue(v, depth + 1); return t ? `${humanize(k)}: ${t}` : ''; })
    .filter(Boolean).join('; ');
}
